/**
 * Fallback currency data — platform-agnostic copy of mobile app data.
 * Used for offline currency info, minor unit lookups, etc.
 *
 * IMPORTANT: keep this in sync with `src/data/currencies-fallback.ts` (mobile).
 * Both files must contain the same currency set, minor units and rates so the
 * mobile and web balance engines convert/display identically when offline or
 * when a currency is missing from the live exchange-rate feed.
 *
 * Last updated: 2025-10-03
 * Source: Supabase get_latest_exchange_rates() + currencies table
 */

export interface FallbackCurrency {
  code: string;
  symbol: string;
  minor_units: number;
  rate_to_usd: number;
}

export const FALLBACK_DATE = '2025-10-03';

export const FALLBACK_CURRENCIES_DATA: FallbackCurrency[] = [
  { code: 'ADA', symbol: '₳', minor_units: 6, rate_to_usd: 0.86247 },
  { code: 'AED', symbol: 'د.إ', minor_units: 2, rate_to_usd: 0.27232373 },
  { code: 'ALGO', symbol: 'ALGO', minor_units: 6, rate_to_usd: 0.2216 },
  { code: 'ALL', symbol: 'L', minor_units: 2, rate_to_usd: 0.012150668 },
  { code: 'AMD', symbol: '֏', minor_units: 2, rate_to_usd: 0.0026120573 },
  { code: 'ARS', symbol: '$', minor_units: 2, rate_to_usd: 0.0007022472 },
  { code: 'ATOM', symbol: 'ATOM', minor_units: 6, rate_to_usd: 4.251 },
  { code: 'AUD', symbol: 'A$', minor_units: 2, rate_to_usd: 0.65971762 },
  { code: 'AVAX', symbol: 'AVAX', minor_units: 8, rate_to_usd: 30.8419 },
  { code: 'AZN', symbol: '₼', minor_units: 2, rate_to_usd: 0.58840837 },
  { code: 'BAM', symbol: 'KM', minor_units: 2, rate_to_usd: 0.599916 },
  { code: 'BCH', symbol: 'BCH', minor_units: 8, rate_to_usd: 595.1 },
  { code: 'BDT', symbol: '৳', minor_units: 2, rate_to_usd: 0.008218953 },
  { code: 'BGN', symbol: 'лв', minor_units: 2, rate_to_usd: 0.60097238 },
  { code: 'BHD', symbol: 'د.ب', minor_units: 3, rate_to_usd: 2.65252 },
  { code: 'BNB', symbol: 'BNB', minor_units: 8, rate_to_usd: 1096.46 },
  { code: 'BND', symbol: 'B$', minor_units: 2, rate_to_usd: 0.775 }, // Fallback estimate, pegged 1:1 to SGD
  { code: 'BOB', symbol: 'Bs', minor_units: 2, rate_to_usd: 0.14598541 },
  { code: 'BRL', symbol: 'R$', minor_units: 2, rate_to_usd: 0.18734311 },
  { code: 'BTC', symbol: '₿', minor_units: 8, rate_to_usd: 120273.62 },
  { code: 'BWP', symbol: 'P', minor_units: 2, rate_to_usd: 0.072 },
  { code: 'BYN', symbol: 'Br', minor_units: 2, rate_to_usd: 0.29498523 },
  { code: 'CAD', symbol: 'C$', minor_units: 2, rate_to_usd: 0.71598876 },
  { code: 'CHF', symbol: 'CHF', minor_units: 2, rate_to_usd: 1.25357 },
  { code: 'CLP', symbol: '$', minor_units: 0, rate_to_usd: 0.0010403229 },
  { code: 'CNY', symbol: '¥', minor_units: 2, rate_to_usd: 0.14047903 },
  { code: 'COP', symbol: '$', minor_units: 2, rate_to_usd: 0.00025751826 },
  { code: 'CRC', symbol: '₡', minor_units: 2, rate_to_usd: 0.0019952114 },
  { code: 'CZK', symbol: 'Kč', minor_units: 2, rate_to_usd: 0.0482665 },
  { code: 'DAI', symbol: 'DAI', minor_units: 8, rate_to_usd: 0.99970001 },
  { code: 'DKK', symbol: 'kr', minor_units: 2, rate_to_usd: 0.15707044 },
  { code: 'DOGE', symbol: 'Ð', minor_units: 8, rate_to_usd: 0.25925 },
  { code: 'DOP', symbol: 'RD$', minor_units: 2, rate_to_usd: 0.016051365 },
  { code: 'DOT', symbol: 'DOT', minor_units: 10, rate_to_usd: 4.231 },
  { code: 'DZD', symbol: 'د.ج', minor_units: 2, rate_to_usd: 0.0075 }, // Fallback estimate
  { code: 'EGP', symbol: 'E£', minor_units: 2, rate_to_usd: 0.02096436 },
  { code: 'ETB', symbol: 'Br', minor_units: 2, rate_to_usd: 0.0069444444 },
  { code: 'ETC', symbol: 'ETC', minor_units: 8, rate_to_usd: 19.87 },
  { code: 'ETH', symbol: 'Ξ', minor_units: 8, rate_to_usd: 4508.51 },
  { code: 'EUR', symbol: '€', minor_units: 2, rate_to_usd: 1.17244 },
  { code: 'FJD', symbol: 'FJ$', minor_units: 2, rate_to_usd: 0.445 }, // Fallback estimate
  { code: 'GBP', symbol: '£', minor_units: 2, rate_to_usd: 1.34401 },
  { code: 'GEL', symbol: '₾', minor_units: 2, rate_to_usd: 0.36821562 },
  { code: 'GHS', symbol: '₵', minor_units: 2, rate_to_usd: 0.079999998 },
  { code: 'GTQ', symbol: 'Q', minor_units: 2, rate_to_usd: 0.13054831 },
  { code: 'HKD', symbol: 'HK$', minor_units: 2, rate_to_usd: 0.12851323 },
  { code: 'HNL', symbol: 'L', minor_units: 2, rate_to_usd: 0.038256131 },
  { code: 'HUF', symbol: 'Ft', minor_units: 2, rate_to_usd: 0.0030130465 },
  { code: 'IDR', symbol: 'Rp', minor_units: 2, rate_to_usd: 0.000060215571 },
  { code: 'ILS', symbol: '₪', minor_units: 2, rate_to_usd: 0.30190504 },
  { code: 'INR', symbol: '₹', minor_units: 2, rate_to_usd: 0.011272559 },
  { code: 'ISK', symbol: 'kr', minor_units: 0, rate_to_usd: 0.0082549117 },
  { code: 'JMD', symbol: 'J$', minor_units: 2, rate_to_usd: 0.0062900996 },
  { code: 'JOD', symbol: 'د.أ', minor_units: 3, rate_to_usd: 1.41243 },
  { code: 'JPY', symbol: '¥', minor_units: 0, rate_to_usd: 0.0067731403 },
  { code: 'KES', symbol: 'KSh', minor_units: 2, rate_to_usd: 0.0077375422 },
  { code: 'KHR', symbol: '៛', minor_units: 2, rate_to_usd: 0.00025 }, // Fallback estimate
  { code: 'KRW', symbol: '₩', minor_units: 0, rate_to_usd: 0.00071006594 },
  { code: 'KWD', symbol: 'د.ك', minor_units: 3, rate_to_usd: 3.27547 },
  { code: 'KYD', symbol: 'CI$', minor_units: 2, rate_to_usd: 1.2 }, // Pegged to USD at CI$1 = US$1.20
  { code: 'KZT', symbol: '₸', minor_units: 2, rate_to_usd: 0.0018297927 },
  { code: 'LAK', symbol: '₭', minor_units: 2, rate_to_usd: 0.000046 }, // Fallback estimate
  { code: 'LBP', symbol: 'ل.ل', minor_units: 2, rate_to_usd: 0.000011173184 },
  { code: 'LINK', symbol: 'LINK', minor_units: 8, rate_to_usd: 22.7109 },
  { code: 'LKR', symbol: 'Rs', minor_units: 2, rate_to_usd: 0.0033080815 },
  { code: 'LTC', symbol: 'Ł', minor_units: 8, rate_to_usd: 117.58 },
  { code: 'MAD', symbol: 'د.م', minor_units: 2, rate_to_usd: 0.10977189 },
  { code: 'MATIC', symbol: 'MATIC', minor_units: 8, rate_to_usd: 0.45 }, // Fallback estimate
  { code: 'MDL', symbol: 'L', minor_units: 2, rate_to_usd: 0.060459489 },
  { code: 'MKD', symbol: 'ден', minor_units: 2, rate_to_usd: 0.019094902 },
  { code: 'MMK', symbol: 'K', minor_units: 2, rate_to_usd: 0.00048 }, // Fallback estimate
  { code: 'MOP', symbol: 'MOP$', minor_units: 2, rate_to_usd: 0.124 }, // Fallback estimate
  { code: 'MUR', symbol: 'Rs', minor_units: 2, rate_to_usd: 0.022 }, // Fallback estimate
  { code: 'MVR', symbol: 'Rf', minor_units: 2, rate_to_usd: 0.065 }, // Fallback estimate
  { code: 'MXN', symbol: '$', minor_units: 2, rate_to_usd: 0.054299135 },
  { code: 'MYR', symbol: 'RM', minor_units: 2, rate_to_usd: 0.23747328 },
  { code: 'NAD', symbol: 'N$', minor_units: 2, rate_to_usd: 0.0578 }, // Fallback estimate, pegged 1:1 to ZAR
  { code: 'NGN', symbol: '₦', minor_units: 2, rate_to_usd: 0.00068086034 },
  { code: 'NIO', symbol: 'C$', minor_units: 2, rate_to_usd: 0.027304277 },
  { code: 'NOK', symbol: 'kr', minor_units: 2, rate_to_usd: 0.10015484 },
  { code: 'NPR', symbol: 'रू', minor_units: 2, rate_to_usd: 0.00705 }, // Fallback estimate, pegged to INR at 1.6:1
  { code: 'NZD', symbol: 'NZ$', minor_units: 2, rate_to_usd: 0.58203834 },
  { code: 'OMR', symbol: 'ر.ع', minor_units: 3, rate_to_usd: 2.5974 },
  { code: 'PAB', symbol: 'B/', minor_units: 2, rate_to_usd: 1.0 }, // Pegged to USD
  { code: 'PEN', symbol: 'S/', minor_units: 2, rate_to_usd: 0.28796038 },
  { code: 'PHP', symbol: '₱', minor_units: 2, rate_to_usd: 0.017266981 },
  { code: 'PKR', symbol: '₨', minor_units: 2, rate_to_usd: 0.0035587189 },
  { code: 'PLN', symbol: 'zł', minor_units: 2, rate_to_usd: 0.27534935 },
  { code: 'PYG', symbol: '₲', minor_units: 0, rate_to_usd: 0.00014176354 },
  { code: 'QAR', symbol: 'ر.ق', minor_units: 2, rate_to_usd: 0.27480077 },
  { code: 'RON', symbol: 'lei', minor_units: 2, rate_to_usd: 0.483 },
  { code: 'RSD', symbol: 'дин', minor_units: 2, rate_to_usd: 0.010046627 },
  { code: 'RUB', symbol: '₽', minor_units: 2, rate_to_usd: 0.012115338 },
  { code: 'RWF', symbol: 'FRw', minor_units: 0, rate_to_usd: 0.00072 },
  { code: 'SAR', symbol: 'ر.س', minor_units: 2, rate_to_usd: 0.26665244 },
  { code: 'SEK', symbol: 'kr', minor_units: 2, rate_to_usd: 0.10634259 },
  { code: 'SGD', symbol: 'S$', minor_units: 2, rate_to_usd: 0.77539819 },
  { code: 'SOL', symbol: 'SOL', minor_units: 9, rate_to_usd: 233.39 },
  { code: 'THB', symbol: '฿', minor_units: 2, rate_to_usd: 0.030854674 },
  { code: 'TND', symbol: 'د.ت', minor_units: 3, rate_to_usd: 0.34542315 },
  { code: 'TRX', symbol: 'TRX', minor_units: 6, rate_to_usd: 0.343383 },
  { code: 'TRY', symbol: '₺', minor_units: 2, rate_to_usd: 0.024003005 },
  { code: 'TTD', symbol: 'TT$', minor_units: 2, rate_to_usd: 0.14924482 },
  { code: 'TWD', symbol: 'NT$', minor_units: 2, rate_to_usd: 0.032867707 },
  { code: 'TZS', symbol: 'TSh', minor_units: 2, rate_to_usd: 0.00041169205 },
  { code: 'UAH', symbol: '₴', minor_units: 2, rate_to_usd: 0.024259185 },
  { code: 'UGX', symbol: 'USh', minor_units: 0, rate_to_usd: 0.00028956971 },
  { code: 'USD', symbol: '$', minor_units: 2, rate_to_usd: 1.0 },
  { code: 'USDC', symbol: '$', minor_units: 6, rate_to_usd: 0.9992 },
  { code: 'USDT', symbol: '₮', minor_units: 6, rate_to_usd: 1.00041 },
  { code: 'UYU', symbol: '$U', minor_units: 2, rate_to_usd: 0.025078369 },
  { code: 'UZS', symbol: 'soʻm', minor_units: 2, rate_to_usd: 0.000079 }, // Fallback estimate
  { code: 'VEF', symbol: 'Bs', minor_units: 2, rate_to_usd: 0.0001 }, // Fallback estimate
  { code: 'VES', symbol: 'Bs.', minor_units: 2, rate_to_usd: 0.0125 }, // Fallback estimate
  { code: 'VND', symbol: '₫', minor_units: 0, rate_to_usd: 0.000037888833 },
  { code: 'XAF', symbol: 'FCFA', minor_units: 0, rate_to_usd: 0.0017889728 },
  { code: 'XLM', symbol: 'XLM', minor_units: 7, rate_to_usd: 0.403599 },
  { code: 'XMR', symbol: 'XMR', minor_units: 8, rate_to_usd: 330.32431 },
  { code: 'XOF', symbol: 'CFA', minor_units: 0, rate_to_usd: 0.0017889728 },
  { code: 'XRP', symbol: 'XRP', minor_units: 6, rate_to_usd: 3.0206 },
  { code: 'XTZ', symbol: 'ꜩ', minor_units: 6, rate_to_usd: 0.7094 },
  { code: 'ZAR', symbol: 'R', minor_units: 2, rate_to_usd: 0.057791442 },
];
